/**
 * Vuex plugin to synchronize state with localStorage across multiple windows
 */

// Storage key prefix to avoid conflicts
const STORAGE_KEY_PREFIX = "chess_";

/**
 * Create a Vuex plugin that syncs specific state to localStorage
 * and listens for changes from other windows
 *
 * @param {Object} options - Configuration options
 * @returns {Function} Vuex plugin
 */
export default function createStorageSyncPlugin(options = {}) {
  // Default state paths to sync (can include module paths like "teacher.classRollModalOpen")
  const statesToSync = options.states || [
    "currentScenario",
    "currentTopicId",
    "scenarioState",
  ];

  // Flag to prevent infinite loops when updating from storage events
  let isUpdatingFromStorage = false;

  return (store) => {
    // Helper function to get nested state value
    const getStateValue = (state, path) => {
      return path.split(".").reduce((obj, key) => obj && obj[key], state);
    };

    // Helper function to get mutation name for a state path
    const getMutationName = (statePath) => {
      const parts = statePath.split(".");
      const lastPart = parts[parts.length - 1];
      return `set${lastPart.charAt(0).toUpperCase() + lastPart.slice(1)}`;
    };

    // Initialize state from localStorage if available
    statesToSync.forEach((statePath) => {
      const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;
      const storedValue = localStorage.getItem(storageKey);

      if (storedValue) {
        try {
          const value = JSON.parse(storedValue);
          const mutationName = getMutationName(statePath);

          if (typeof store.commit === "function") {
            isUpdatingFromStorage = true;
            store.commit(mutationName, value);
            isUpdatingFromStorage = false;
          }
        } catch (error) {
          console.error(`Error parsing stored value for ${statePath}:`, error);
        }
      }
    });

    // Listen for storage events from other windows
    window.addEventListener("storage", (event) => {
      // Check if the event is for one of our synced states
      statesToSync.forEach((statePath) => {
        const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;

        if (event.key === storageKey && event.newValue) {
          try {
            const value = JSON.parse(event.newValue);
            const mutationName = getMutationName(statePath);

            if (typeof store.commit === "function") {
              isUpdatingFromStorage = true;
              store.commit(mutationName, value);
              isUpdatingFromStorage = false;
            }
          } catch (error) {
            console.error(
              `Error handling storage event for ${statePath}:`,
              error
            );
          }
        }
      });
    });

    // Subscribe to store mutations to update localStorage
    store.subscribe((mutation, state) => {
      // Skip if we're currently updating from storage to avoid loops
      if (isUpdatingFromStorage) return;

      // Check if the mutation is for one of our synced states
      statesToSync.forEach((statePath) => {
        const mutationName = getMutationName(statePath);

        if (mutation.type === mutationName) {
          try {
            const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;
            const value = getStateValue(state, statePath);

            if (value !== undefined) {
              localStorage.setItem(storageKey, JSON.stringify(value));
            }
          } catch (error) {
            console.error(`Error storing value for ${statePath}:`, error);
          }
        }
      });
    });
  };
}
